import React from 'react';
import PropTypes from 'prop-types';

import UTRModal from '../../Modal';
import useIsMobile from '../../hooks/useIsMobile';

import styles from './confirmDialog.scss';

// `confirmTestIdSuffix` (default 'confirm-btn') lets a caller keep an already-shipped confirm-button
// testid (e.g. a sibling that exposes '.remove-btn') under CLAUDE.md rule 8.
const ConfirmDialog = ({ title, message, confirmLabel, cancelLabel, confirmTestIdSuffix, destructive, mobileActionSheet, isBusy, onConfirm, onCancel, 'data-testid': dataTestId }) => {
    const { isMobileWidth } = useIsMobile();

    // On mobile a `mobileActionSheet` caller (Delete List) shows the centered iOS-style card that matches
    // the Player List row kebab, not the full-screen modal. Desktop and non-opted-in callers are unchanged.
    if (mobileActionSheet && isMobileWidth) {
        return (
            <div className={styles.actionSheetOverlay} onClick={onCancel} data-testid={dataTestId}>
                <div
                    className={styles.actionSheetCard}
                    onClick={event => event.stopPropagation()}
                    data-testid={dataTestId && `${dataTestId}.sheet`}
                >
                    <div className={styles.actionSheetTitle} data-testid={dataTestId && `${dataTestId}.title`}>
                        {title}
                    </div>
                    <p className={styles.actionSheetMessage} data-testid={dataTestId && `${dataTestId}.message`}>
                        {message}
                    </p>
                    <button
                        type="button"
                        className={`${styles.actionSheetPill} ${destructive ? styles.actionSheetPillDanger : ''}`}
                        onClick={onConfirm}
                        disabled={isBusy}
                        data-testid={dataTestId && `${dataTestId}.${confirmTestIdSuffix}`}
                    >
                        {confirmLabel}
                    </button>
                    <button
                        type="button"
                        className={`${styles.actionSheetPill} ${styles.actionSheetPillCancel}`}
                        onClick={onCancel}
                        disabled={isBusy}
                        data-testid={dataTestId && `${dataTestId}.cancel-btn`}
                    >
                        {cancelLabel}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <UTRModal
            title={title}
            onCloseClick={onCancel}
            className={styles.confirmDialog}
            data-testid={dataTestId}
        >
            <div className={`${styles.desktopTitle} d-none d-md-block`} data-testid={dataTestId && `${dataTestId}.desktop-title`}>
                {title}
            </div>
            <p className={styles.message} data-testid={dataTestId && `${dataTestId}.message`}>
                {message}
            </p>
            <div
                className={`${styles.actions} ${destructive ? styles.actionsDestructive : ''}`}
                data-testid={dataTestId && `${dataTestId}.actions`}
            >
                <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={onCancel}
                    disabled={isBusy}
                    data-testid={dataTestId && `${dataTestId}.cancel-btn`}
                >
                    {cancelLabel}
                </button>
                <button
                    type="button"
                    className={styles.confirmBtn}
                    onClick={onConfirm}
                    disabled={isBusy}
                    data-testid={dataTestId && `${dataTestId}.${confirmTestIdSuffix}`}
                >
                    {confirmLabel}
                </button>
            </div>
        </UTRModal>
    );
};

ConfirmDialog.propTypes = {
    title: PropTypes.string.isRequired,
    message: PropTypes.node.isRequired,
    confirmLabel: PropTypes.string.isRequired,
    cancelLabel: PropTypes.string,
    confirmTestIdSuffix: PropTypes.string,
    // Opt-in destructive treatment (Figma 383-11671): on mobile the buttons stack full-width with the
    // confirm action reading in red on a neutral fill. Only the Delete List modal passes it, so the
    // other ConfirmDialog consumers render unchanged.
    destructive: PropTypes.bool,
    // On mobile, render the centered action-sheet card (Figma 383-11671) instead of the full-screen
    // modal. Desktop is unchanged either way.
    mobileActionSheet: PropTypes.bool,
    isBusy: PropTypes.bool,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    'data-testid': PropTypes.string,
};

ConfirmDialog.defaultProps = {
    cancelLabel: 'Cancel',
    confirmTestIdSuffix: 'confirm-btn',
    destructive: false,
    mobileActionSheet: false,
    isBusy: false,
    'data-testid': null,
};

export default ConfirmDialog;
